import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Step1_BasicInfo from "./Step1_BasicInfo";
import Step2_CompanyInfo, { type Step2FormValues } from "./Step2_CompanyInfo";
import Step3_FleetManager, {
  type Step3FormValues,
} from "./Step3_FleetManager";
import Step4_PaymentMethod, {
  type Step4PaymentFormValues,
} from "./Step4_PaymentMethod";
import Step5_ContactPerson, {
  type Step5ContactFormValues,
} from "./Step5_ContactPerson";
import Step6_CompanyVerification, {
  type Step6FormValues,
} from "./Step6_CompanyVerification";

import type { Step1Input } from "./../../../shared/schemas/index";
import {
  RateLimitRetryError,
  TemporaryServerError,
  TemporaryNetworkError,
  MaintenanceModeError,
  submitCompanyForm,
} from "../api/onboardingApi";

import { AtlanteDialog, GreenSpinnerOverlay } from "@/shared/ui";

const TOTAL_STEPS = 6;

type Step1Data = Step1Input;
type Step2Data = Step2FormValues;
type Step3Data = Step3FormValues;
type Step4Data = Step4PaymentFormValues;
type Step5Data = Step5ContactFormValues;
type Step6Data = Step6FormValues;

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [step, setStep] = useState(1);

  const [step1Data, setStep1Data] = useState<Step1Data | null>(null);
  const [step2Data, setStep2Data] = useState<Step2Data | null>(null);
  const [step3Data, setStep3Data] = useState<Step3Data | null>(null);
  const [step4Data, setStep4Data] = useState<Step4Data | null>(null);
  const [step5Data, setStep5Data] = useState<Step5Data | null>(null);
  const [step6Data, setStep6Data] = useState<Step6Data | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState(
    t("onboarding.common.temporaryIssue.message")
  );

  const goNext = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const goPrev = () => setStep((s) => Math.max(s - 1, 1));

  const closeDialog = () => setDialogOpen(false);

  const overlayLabel = useMemo(() => t("onboarding.common.submitting"), [t]);

  const temporaryMessage = useMemo(
    () => t("onboarding.common.temporaryIssue.message"),
    [t]
  );

  const renderStep = () => {
    if (step === 1) {
      return (
        <Step1_BasicInfo
          currentStep={1}
          totalSteps={TOTAL_STEPS}
          initialValues={step1Data ?? undefined}
          onCompleted={(data) => {
            setStep1Data(data);
            goNext();
          }}
        />
      );
    }

    if (step === 2) {
      return (
        <Step2_CompanyInfo
          currentStep={2}
          totalSteps={TOTAL_STEPS}
          initialValues={step2Data ?? undefined}
          onBack={goPrev}
          onCompleted={(data) => {
            setStep2Data(data);
            goNext();
          }}
        />
      );
    }

    if (step === 3) {
      return (
        <Step3_FleetManager
          currentStep={3}
          totalSteps={TOTAL_STEPS}
          initialValues={step3Data ?? undefined}
          onBack={() => setStep(2)}
          onCompleted={(data) => {
            setStep3Data(data);
            goNext();
          }}
        />
      );
    }

    if (step === 4) {
      return (
        <Step4_PaymentMethod
          currentStep={4}
          totalSteps={TOTAL_STEPS}
          initialValues={step4Data ?? undefined}
          onBack={() => setStep(3)}
          onCompleted={(data) => {
            setStep4Data(data);
            goNext();
          }}
        />
      );
    }

    if (step === 5) {
      return (
        <Step5_ContactPerson
          currentStep={5}
          totalSteps={TOTAL_STEPS}
          initialValues={step5Data ?? undefined}
          onBack={() => setStep(4)}
          onCompleted={(data) => {
            setStep5Data(data);
            goNext();
          }}
        />
      );
    }

    if (step === 6) {
      return (
        <Step6_CompanyVerification
          currentStep={6}
          totalSteps={TOTAL_STEPS}
          initialValues={step6Data ?? undefined}
          onBack={() => setStep(5)}
          onCompleted={(data) => {
            setStep6Data(data);

            if (!step1Data || !step2Data || !step3Data || !step4Data || !step5Data) {
              console.error(
                "[OnboardingWizard] Missing step data, cannot submit company."
              );
              return;
            }

            const taxId = step2Data.vatNumber;
            if (!taxId) {
              console.error(
                "[OnboardingWizard] Missing taxId (vatNumber) in step2, cannot submit company."
              );
              return;
            }

            (async () => {
              setSubmitting(true);
              try {
                await submitCompanyForm({
                  taxId,
                  formData: {
                    step1: step1Data,
                    step2: step2Data,
                    step3: step3Data,
                    step4: step4Data,
                    step5: step5Data,
                    step6: data,
                  },
                });

                navigate("/thank-you");
              } catch (err) {
                console.error(
                  "[OnboardingWizard] Error while submitting company form:",
                  err
                );

                // 429 retry exhausted => dialog
                if (err instanceof RateLimitRetryError) {
                  setDialogMessage(temporaryMessage);
                  setDialogOpen(true);
                  return;
                }

                // 503 => maintenance page
                if (err instanceof MaintenanceModeError) {
                  navigate("/maintenance");
                  return;
                }

                // >=500 => maintenance page
                if (err instanceof TemporaryServerError) {
                  navigate("/maintenance");
                  return;
                }

                // Network/CORS/timeout => dialog (NON maintenance)
                if (err instanceof TemporaryNetworkError) {
                  setDialogMessage(temporaryMessage);
                  setDialogOpen(true);
                  return;
                }

                // Tutto il resto => dialog
                setDialogMessage(temporaryMessage);
                setDialogOpen(true);
              } finally {
                setSubmitting(false);
              }
            })();
          }}
        />
      );
    }

    return null;
  };

  return (
    <>
      {renderStep()}

      <GreenSpinnerOverlay open={submitting} label={overlayLabel} />

      <AtlanteDialog
        open={dialogOpen}
        title={t("onboarding.common.temporaryIssue.title")}
        message={dialogMessage}
        onClose={closeDialog}
        closeLabel={t("onboarding.common.temporaryIssue.ok")}
      />
    </>
  );
}
